import { createServerFn } from "@tanstack/react-start";
import { setResponseHeader } from "@tanstack/react-start/server";
import { createClient } from "@supabase/supabase-js";
import { CATEGORIES, CATEGORY_SLUGS, LANDING_LIMIT, LANDING_STATUSES, type CategorySlug, type LandingUnit } from "@/lib/categoryLanding";
import { cachedWithTimeout } from "@/lib/categoryLandingCache";
import { unitPhotos } from "@/lib/rvPhotoPolicy";
import { incomingUnitsLast } from "@/lib/inventoryOrdering";

async function loadUnits(slug: CategorySlug): Promise<LandingUnit[]> {
  const url = process.env["SUPABASE_URL"] || import.meta.env["VITE_SUPABASE_URL"];
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"] || import.meta.env["VITE_SUPABASE_PUBLISHABLE_KEY"];
  if (!url || !key) throw new Error("landing: missing config");
  const client = createClient(url, key, {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
    global: {
      fetch: (input, init) => {
        const headers = new Headers(init?.headers);
        if (key.startsWith("sb_publishable_") && headers.get("Authorization") === `Bearer ${key}`) headers.delete("Authorization");
        headers.set("apikey", key);
        return fetch(input, { ...init, headers, signal: AbortSignal.timeout(5000) });
      },
    },
  });
  const cfg = CATEGORIES[slug];
  let q = client
    .from("rv_inventory")
    .select("slug,stock_number,title,year,rv_type,condition,status,price,sale_price,images")
    .in("status", LANDING_STATUSES)
    .order("price", { ascending: true, nullsFirst: false })
    .limit(LANDING_LIMIT * 2);
  if (cfg.condition) q = q.eq("condition", cfg.condition);
  if (cfg.types.length) q = q.in("rv_type", cfg.types);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  const rows = incomingUnitsLast((data ?? []) as Array<Omit<LandingUnit, "image"> & { images: string[] | null }>);
  return rows.slice(0, LANDING_LIMIT).map(({ images, ...r }) => ({ ...r, image: unitPhotos(images)[0] ?? null }));
}

export const getCategoryLanding = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => {
    if (!CATEGORY_SLUGS.includes(slug as CategorySlug)) throw new Error("Unknown category");
    return slug as CategorySlug;
  })
  .handler(async ({ data: slug }) => {
    const start = Date.now();
    const { value, path } = await cachedWithTimeout(`landing:${slug}`, () => loadUnits(slug));
    try {
      setResponseHeader("Server-Timing", `landing;dur=${Date.now() - start};desc="${path}"`);
    } catch {
      /* no header context */
    }
    return { units: value ?? [], unavailable: value === null };
  });
